import { useFeatureGate } from '@statsig/react-bindings';
import { secondsToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { getTelehealthConfig } from '../../config';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { isWhiteboardModeAtom } from '../../session/messages/state';
import { useSendWhiteboardSnapshotCallback } from '../../session/messages/useSendWhiteboardSnapshotCallback';
import { whiteboardEnabledGate, whiteboardEventualConsistencyGate } from '../../statsig/gates';
import { UserType } from '../../types';
import { providerShortIdAtom } from '../state';

/**
 * Periodically sends the provider's whiteboard snapshots to all participants,
 * aiming for eventual consistency with the provider's whiteboard state.
 */
export function usePeriodicallySendWhiteboardSnapshots() {
  const sendWhiteboardSnapshot = useSendWhiteboardSnapshotCallback();
  const getProviderShortId = useAtomCallbackMemoized((get) => get(providerShortIdAtom), []);
  const isWhiteboardMode = useAtomValue(isWhiteboardModeAtom).value;
  const { value: isWhiteboardingEnabled } = useFeatureGate(whiteboardEnabledGate);
  const { value: isWhiteboardingEventualConsistencyEnabled } = useFeatureGate(whiteboardEventualConsistencyGate);
  const isEnabled = isWhiteboardingEnabled && isWhiteboardingEventualConsistencyEnabled;
  const dynamicIntervalSeconds = getTelehealthConfig().whiteboard_eventual_consistency_interval_seconds;
  const intervalMs = secondsToMilliseconds(dynamicIntervalSeconds);

  useEffect(
    function handleSendingInterval() {
      if (!isWhiteboardMode || !isEnabled) return;

      const interval = setInterval(async () => {
        const providerShortId = getProviderShortId();
        if (!providerShortId) return;
        await sendWhiteboardSnapshot({
          entityId: providerShortId,
          entityType: UserType.PROVIDER,
        });
      }, intervalMs);

      return () => {
        clearInterval(interval);
      };
    },
    [intervalMs, isEnabled, isWhiteboardMode, getProviderShortId, sendWhiteboardSnapshot],
  );
}
